import { createContext, ReactNode, useContext } from "react";
import classNames from "classnames";

import Accordion from "features/components/Accordion";
import Typography from "features/components/Typography";
import CounterIcon from "features/ui/Icons/CounterIcon";

import { splitOrdinal, splitSummary } from "./utils";

const NestedAccordionContext = createContext(false);

interface Props {
  open?: boolean;
  children?: ReactNode;
}

// Renders a markdown <details> block as the design-system Accordion: the
// <summary> child becomes the title, everything else the body. Nested blocks
// render as small outlined rows inside the mixed outer one.
const MarkdownAccordion = ({ open, children }: Props) => {
  const nested = useContext(NestedAccordionContext);
  const { hasSummary, summaryChildren, content } = splitSummary(children);
  const { ordinal, title } = nested
    ? { title: summaryChildren }
    : splitOrdinal(summaryChildren);

  // Without a summary there is nothing to label the collapse with, so show
  // the body directly.
  if (!hasSummary) {
    return (
      <NestedAccordionContext.Provider value>
        {content}
      </NestedAccordionContext.Provider>
    );
  }

  const accordionTitle = (
    <>
      {ordinal && (
        <>
          <span className="sr-only">{ordinal}. </span>
          <CounterIcon
            value={ordinal}
            aria-hidden
            className="text-blue-400 mr-2.5 shrink-0"
            testId={`counter-icon-${ordinal}`}
          />
        </>
      )}
      <Typography variant="h5">{title}</Typography>
    </>
  );

  return (
    <NestedAccordionContext.Provider value>
      <Accordion
        title={accordionTitle}
        variant={nested ? "outlined" : "mixed"}
        size={nested ? "small" : "medium"}
        defaultExpanded={Boolean(open)}
        className={classNames({ "my-2": nested, "mb-4": !nested })}
      >
        <>{content}</>
      </Accordion>
    </NestedAccordionContext.Provider>
  );
};

export default MarkdownAccordion;
